import { Outlet, createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { getConfigValue } from '~/helpers/envHelpers'
import { useCurrentOrganization } from '~/hooks'

import { JobContext } from './v2/-context'

const searchSchema = z.object({
  jobId: z.string().nullish(),
})

export const Route = createFileRoute('/_authenticated/$alias/v2')({
  component: RouteComponent,
  validateSearch: searchSchema,
})

function RouteComponent() {
  const { jobId } = Route.useSearch()
  const organization = useCurrentOrganization()

  const { status, send, subscribe } = useWebSocket(getConfigValue('VITE_TRPC_WS_URL'), {
    onMessage: (message) => {
      console.log(message)
    },
  })

  useEffect(() => {
    if (jobId && organization && status === 'open') {
      // send({
      //   type: 'start_job',
      //   jobId: jobId,
      //   organizationId: organization.id,
      // })
    }
  }, [jobId, status])

  const value = useMemo(
    () => ({ status, send, subscribe, jobId }),
    [status, send, subscribe, jobId],
  )

  return (
    <JobContext value={value}>
      <Outlet />
    </JobContext>
  )
}

export function useWebSocket(
  url: string,
  {
    onMessage,
    reconnectDelay = 2000,
  }: { onMessage?: (data: any) => void; reconnectDelay?: number } = {},
) {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<number | null>(null)
  const onMessageRef = useRef(onMessage)
  const shouldReconnectRef = useRef(true)
  const subscribersRef = useRef(new Set())
  const [status, setStatus] = useState<'connecting' | 'open' | 'closed'>('connecting') // connecting | open | closed

  // Keep latest onMessage without re-triggering the connect effect
  useEffect(() => {
    onMessageRef.current = onMessage
  }, [onMessage])

  useEffect(() => {
    shouldReconnectRef.current = true

    const connect = () => {
      const ws = new WebSocket(url)
      wsRef.current = ws
      setStatus('connecting')

      ws.onopen = () => setStatus('open')
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          onMessageRef.current?.(data)
        } catch {
          onMessageRef.current?.(event.data)
        }
      }
      ws.onclose = () => {
        setStatus('closed')
        if (shouldReconnectRef.current) {
          reconnectTimerRef.current = window.setTimeout(connect, reconnectDelay)
        }
      }
      ws.onerror = () => ws.close() // let onclose drive reconnect
    }

    connect()

    return () => {
      shouldReconnectRef.current = false
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      wsRef.current?.close()
    }
  }, [url, reconnectDelay])

  const send = useCallback((data: any) => {
    const ws = wsRef.current
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(typeof data === 'string' ? data : JSON.stringify(data))
      return true
    }
    return false
  }, [])

  const subscribe = useCallback((handler: () => void) => {
    subscribersRef.current.add(handler)
    return () => subscribersRef.current.delete(handler)
  }, [])

  return { status, send, subscribe }
}
